import { useRef, useEffect } from 'react';
import {
  Bot,
  Scale,
  FileText,
  Brain,
  MessageSquare,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';

const features = [
  {
    icon: FileText,
    title: 'Analise qualquer documento',
    description: 'Revise documentos e avalie riscos com precisão em minutos, utilizando tecnologia de IA avançada.',
    delay: 0.2,
  },
  {
    icon: Bot,
    title: 'Criação de Documentos Jurídicos',
    description: 'Elabore documentos jurídicos fudamentados e precisos com modelos inteligentes e automação.',
    delay: 0.3,
  },
  {
    icon: Brain,
    title: 'Estratégias para Casos',
    description: 'Obtenha insights baseados em dados para construir estratégias jurídicas vencedoras.',
    delay: 0.4,
  },
  {
    icon: Scale,
    title: 'Avaliação de Riscos',
    description: 'Identifique e mitigue riscos jurídicos antes que se tornem problemas.',
    delay: 0.5,
  },
  {
    icon: MessageSquare,
    title: 'IA jurídica no Whatsapp 24/7',
    description: 'Respostas instantâneas para as dúvidas jurídicas avançadas por texto ou áudio.',
    delay: 0.6,
  },
  {
    icon: BookOpen,
    title: 'Pesquisa de Jurisprudência e Doutrinas',
    description: 'Encontre jurisprudências e doutrinas, de fato relevantes, em segundos com busca otimizada por IA.',
    delay: 0.7,
  },
];


export default function Features() {
  const featuresRef = useRef(null);
  const isInView = useInView(featuresRef, { once: true, margin: '-100px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  useEffect(() => {
    const urlParams = window.location.search;
    const button = document.getElementById('featuresCtaButton') as HTMLAnchorElement;

    if (urlParams && button) {
      const cleanParams = urlParams.startsWith('?') ? urlParams.substring(1) : urlParams;
      const buttonUrl = new URL(button.href);
      buttonUrl.search = cleanParams;
      button.href = buttonUrl.toString();
    }
  }, []);

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute -left-40 top-0 w-[400px] h-[400px] rounded-full bg-[#6942E2] opacity-20 blur-3xl" />

      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Por que escolher a LawX?</h2>
          <p className="text-xl text-gray-300">Soluções personalizadas para sua necessidade</p>
        </div>

        <div ref={featuresRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
                variants={{
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      delay: feature.delay,
                      ease: 'easeOut',
                    },
                  },
                }}
                className="group backdrop-blur-lg bg-white/5 rounded-lg p-6 border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-[#28E7C5]/30"
              >
                <Icon className="w-12 h-12 mb-4 text-[#2D93F8]" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            );
          })}
          <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center mt-12">
            {/* CTA Button with shining effect */}
            <a 
              id="featuresCtaButton"
                              href="https://plataforma.lawx.ai/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <button
                className="group relative px-8 py-4 
                           bg-gradient-to-r from-[#4FC3F7] to-[#0B63F8] 
                           text-white rounded-lg font-semibold text-lg 
                           transition-all duration-300 
                           hover:shadow-[0_0_20px_rgba(11,99,248,0.4)] 
                           hover:scale-105 mx-auto overflow-hidden"
              >
                Teste grátis!
                <ArrowRight className="inline-block ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent 
                             opacity-0 group-hover:opacity-100 
                             transform -translate-x-full skew-x-12 
                             group-hover:translate-x-full 
                             transition-transform duration-700 ease-in-out pointer-events-none"
                />
              </button>
            </a>

          </div>
        </div>
      </div>
    </section>
  );
}
